import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import {
  useActions,
  useActionsCatalog,
  useArchiveAction,
  useCreateAction,
  useUpdateAction,
} from "@/hooks/useActions";

import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";
import { toTitleCase } from "@/utils/toTitleCase";

import { Action, ActionPayload } from "@/domain/actions.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

import ActionFilters, { Filters } from "@/ui/content/actions/Filters";
import ActionModal from "@/ui/modals/actions/ActionModal";

const DEFAULT_FILTERS: Filters = {
  sortField: "order",
  sortOrder: 1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  status: "",
  type: "",
};

const ActionsPage: FC = () => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { setPageTitle } = usePageStore();

  const filtersState = useState<Filters>(DEFAULT_FILTERS);
  const [filters, setFilters] = filtersState;
  const debouncedSearch = useDebounce({ value: filters.search });

  const [modalState, setModalState] = useState<{
    open: boolean;
    action?: Action;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    action?: Action;
  }>({ open: false });

  const { actions, isLoadingActions, isFetchingActions, refetchActions } =
    useActions({
      sortField: filters.sortField,
      sortOrder: filters.sortOrder,
      pageNumber: filters.pageNumber,
      pageSize: filters.pageSize,
      search: debouncedSearch,
      status: filters.status,
      type: filters.type,
    });

  const createAction = useCreateAction();
  const updateAction = useUpdateAction();
  const archiveAction = useArchiveAction();
  const createAuditLog = useCreateAuditlog();

  const { actionsCatalog } = useActionsCatalog();

  const actionNameById = useMemo(() => {
    const map = new Map<string, string>();
    (actionsCatalog?.list || []).forEach((a) => {
      if (a._id) map.set(a._id, a.name || a._id);
    });
    return map;
  }, [actionsCatalog]);

  const isSaving = createAction.isPending || updateAction.isPending;

  useEffect(() => {
    setPageTitle("Actions");
  }, []);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.ACTIONS,
    option: OPTIONS.ACTIONS_LIST,
    actionCode: ACTION_CODE.WEB_ACTION_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the list of actions",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    programId: userInfo?.programId as string,
    orgUnitId: userInfo?.orgUnitIds?.[0] as string,
  });

  useEffect(() => {
    refetchActions();
  }, [
    debouncedSearch,
    filters.status,
    filters.type,
    filters.sortField,
    filters.sortOrder,
    filters.pageNumber,
    filters.pageSize,
  ]);

  useEffect(() => {
    if (debouncedSearch !== "") {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ACTIONS,
        option: OPTIONS.ACTIONS_LIST,
        actionCode: ACTION_CODE.WEB_ACTION_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the list of actions",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
    }
  }, [debouncedSearch]);

  const handleOpenCreate = () => {
    setModalState({ open: true, action: undefined });
  };

  const handleOpenEdit = (action: Action) => {
    setModalState({ open: true, action });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, action: undefined });
  };

  const handleSave = async (data: ActionPayload) => {
    const editing = modalState.action;

    try {
      if (editing) {
        await updateAction.mutateAsync({ id: editing._id, payload: data });

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ACTIONS,
          option: OPTIONS.ACTION_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_ACTION_EDIT,
          action: "When editing an action",
          detail: `Edited action ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The action has been updated.", "success");
      } else {
        await createAction.mutateAsync(data);

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ACTIONS,
          option: OPTIONS.ACTION_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_ACTION_ADD,
          action: "When creating an action",
          detail: `Created action ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The action has been created.", "success");
      }

      handleCloseModal();
      refetchActions();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.action;
    if (!target) return;

    try {
      await archiveAction.mutateAsync(target._id);

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ACTIONS,
        option: OPTIONS.DELETE,
        actionCode: ACTION_CODE.WEB_ACTION_DELETE,
        action: "When deleting an action",
        detail: `Deleted action ${target.name}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
      refetchActions();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "200px",
    },
    {
      id: "code",
      isSortable: true,
      disablePadding: false,
      label: "Code",
      align: "left",
      width: "240px",
    },
    {
      id: "type",
      isSortable: true,
      disablePadding: false,
      label: "Type",
      align: "left",
      width: "140px",
    },
    {
      id: "order",
      isSortable: true,
      disablePadding: false,
      label: "Order",
      align: "left",
      width: "100px",
    },
    {
      id: "subActions",
      isSortable: false,
      disablePadding: false,
      label: "Sub actions",
      align: "left",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "120px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "right",
      width: "100px",
    },
  ];

  const rows = useMemo(() => {
    if (!actions || actions.list.length === 0) return [];

    return actions.list.map((row, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(row),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => setDeleteAlert({ open: true, action: row }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const subActionIds: string[] = Array.isArray(row.subActions)
        ? row.subActions
            .map((sa) => (typeof sa === "string" ? sa : (sa?._id ?? "")))
            .filter(Boolean)
        : [];

      const subActionNames = subActionIds
        .map((id) => actionNameById.get(id) || id)
        .filter(Boolean);

      return createDataRow(index, {
        name: <div>{row.name}</div>,
        code: <div>{row.code}</div>,
        type: <div>{row.type || "-"}</div>,
        order: <div>{row.order ?? "-"}</div>,
        subActions:
          subActionNames.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ rowGap: 0.5 }}
            >
              {subActionNames.map((name, i) => (
                <Chip
                  key={`${row._id}-sub-${i}`}
                  size="small"
                  label={<div className="text-xs">{name}</div>}
                />
              ))}
            </Stack>
          ) : (
            <div>-</div>
          ),
        status: row.status ? (
          <Chip
            sx={{
              bgcolor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.light
                  : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.main
                  : theme.palette.warning.dark,
            }}
            label={<div className="text-xs">{toTitleCase(row.status)}</div>}
          />
        ) : (
          <div>-</div>
        ),
        action: <ActionMenu id={row._id} options={actionMenu} />,
      });
    });
  }, [actions, theme, actionNameById]);

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Actions
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <ActionFilters filtersState={filtersState} />

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={actions?.totalCount}
                  isLoading={isLoadingActions || isFetchingActions}
                  messageEmptyData="No actions to show."
                  onChangePage={(_event, newPage) => {
                    setFilters((prev) => ({ ...prev, pageNumber: newPage }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => {
                      const newSortField = orderBy as string;
                      const newSortOrder = order === "asc" ? 1 : -1;
                      if (
                        prev.sortField === newSortField &&
                        prev.sortOrder === newSortOrder
                      ) {
                        return prev;
                      }

                      return {
                        ...prev,
                        pageNumber: 0,
                        sortField: newSortField,
                        sortOrder: newSortOrder,
                      };
                    });
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <ActionModal
          open={modalState.open}
          onClose={handleCloseModal}
          action={modalState.action}
          onSave={handleSave}
          isPending={isSaving}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveAction.isPending}
          theme={theme}
        />
      )}
    </>
  );
};

export default ActionsPage;
